import { useSignIn, useAuth, useClerk } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageSelector } from './language-selector';
import { useAcquisitionTracking } from '@/hooks/use-acquisition-tracking';
import { brandingConfig } from '@/config/branding';
import { BrandLogo } from '@/components/branding/brand-logo';
import { useResolveSignInBrand } from '@/hooks/use-resolve-sign-in-brand';
import { knownPartnerByOrigin } from '@/config/current-partner';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { crmV3SignUpUrlFor } from '@/hooks/use-crm-v3-redirect';
import { useSafeRedirectUrl, useSafeReturnToPath } from '@/hooks/use-safe-redirect-url';
import { config } from '@/lib/config';
import { pickEmailCodeFactor } from '@/lib/auth/email-code-factor';

/**
 * Send the (already authenticated) user back to a satellite (e.g.
 * paydash.tagada.io, payment.tagada.io, dashboard.tagadapay.io) using Clerk's handshake-aware
 * URL builder. A plain `window.location.replace(satelliteUrl)` will land
 * on the satellite WITHOUT a `__clerk_handshake=...` query param, so the
 * satellite SDK has no session token to materialize — it bounces back to
 * the primary's /sign-in and we get an infinite primary↔satellite loop.
 *
 * `clerk.buildUrlWithAuth` appends the short-lived handshake token the
 * satellite needs to consume on first load to set its own session
 * cookies. This is the same mechanism Clerk uses internally for
 * `redirectWithAuth`.
 */
function redirectToSatelliteWithAuth(
  clerk: ReturnType<typeof useClerk>,
  partnerUrl: string,
) {
  try {
    const urlWithAuth = clerk.buildUrlWithAuth(partnerUrl);
    window.location.replace(urlWithAuth);
  } catch {
    // If Clerk isn't fully loaded yet, fall back to a raw redirect.
    // The satellite will bounce back here and the next pass will pick
    // up the handshake once Clerk is ready.
    window.location.replace(partnerUrl);
  }
}

/** Register partner OTP branding on the API before Clerk sends the code. */
async function registerPartnerOtpBranding(email: string, partnerRedirectUrl: string | null) {
  if (!partnerRedirectUrl) return;
  const partner = knownPartnerByOrigin(partnerRedirectUrl);
  if (!partner) return;

  try {
    await fetch(`${config.apiBaseUrl}/api/public/crm/register-otp-branding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        partnerAppHost: partner.appHost,
      }),
    });
  } catch (err) {
    console.warn('Failed to register partner OTP branding', err);
  }
}

export function CustomSignIn() {
  const { t } = useTranslation('auth');
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn } = useAuth();
  const clerk = useClerk();
  const navigate = useNavigate();
  const partnerRedirectUrl = useSafeRedirectUrl();
  const returnToPath = useSafeReturnToPath();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);

  // When a partner satellite bounced the user here with ?redirect_url=,
  // re-skin the page with that partner's brand so the hop stays visually
  // consistent with the partner app. Falls back to the ambient brand.
  const brand = useResolveSignInBrand(partnerRedirectUrl);
  const isPartnerBrand = brand !== brandingConfig;
  const signUpTarget = (!isPartnerBrand && crmV3SignUpUrlFor(window.location.hostname)) || '/sign-up-new';

  // Apply brand theme (override when partner redirect present)
  useBrandTheme(brand);

  // Track acquisition data (UTM params, referrer, etc.)
  useAcquisitionTracking();

  // Redirect to home (or partner origin) if already signed in.
  // For partner satellites, attach Clerk's handshake token so the
  // satellite SDK can materialize the session cookies on landing —
  // otherwise the satellite has no session and bounces back here, looping.
  useEffect(() => {
    if (!isSignedIn) return;
    if (partnerRedirectUrl) {
      redirectToSatelliteWithAuth(clerk, partnerRedirectUrl);
      return;
    }
    navigate(returnToPath || '/');
  }, [isSignedIn, navigate, partnerRedirectUrl, returnToPath, clerk]);

  // OTP sign-in - Send code
  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.create({
        identifier: email,
      });

      const emailCodeFactor = pickEmailCodeFactor(result.supportedFirstFactors, email);

      if (emailCodeFactor) {
        await registerPartnerOtpBranding(email, partnerRedirectUrl);
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: emailCodeFactor.emailAddressId,
        });
        setVerifyingOTP(true);
      } else {
        setError(t('signIn.errors.emailCodeUnavailable'));
      }
    } catch (err: any) {
      console.error('OTP send error:', err);
      setError(err.errors?.[0]?.message || t('signIn.errors.sendCodeFailed'));
    } finally {
      setLoading(false);
    }
  };

  // OTP sign-in - Verify code
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        if (partnerRedirectUrl) {
          redirectToSatelliteWithAuth(clerk, partnerRedirectUrl);
          return;
        }
        navigate(returnToPath || '/');
      }
    } catch (err: any) {
      console.error('OTP verify error:', err);
      setError(err.errors?.[0]?.message || t('signIn.errors.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth sign-in
  const handleGoogleSignIn = async () => {
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      if (partnerRedirectUrl) {
        // OAuth goes primary -> Google -> /sso-callback -> SSOCallbackProcessor
        // forces a fixed signInForceRedirectUrl, which would strip our
        // redirectUrlComplete. Persist partner intent via sessionStorage
        // (same primary origin throughout) so SSOSigninRedirect can bounce
        // the user back to the partner host after the session is active.
        sessionStorage.setItem('partner_redirect_url', partnerRedirectUrl);
      } else {
        sessionStorage.removeItem('partner_redirect_url');
      }
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: partnerRedirectUrl || '/',
      });
    } catch (err: any) {
      console.error('Google sign in error:', err);
      setError(err.errors?.[0]?.message || t('signIn.errors.googleSignInFailed'));
      setLoading(false);
    }
  };

  // OTP Verification screen
  if (verifyingOTP) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <BrandLogo brand={brand} />
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-foreground text-3xl font-bold tracking-tight">
              {t('signIn.otpVerification.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('signIn.otpVerification.subtitle')} <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('signIn.otpVerification.codeLabel')}</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('signIn.otpVerification.codePlaceholder')}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                required
                disabled={loading}
              />
              <p className="text-muted-foreground text-xs">{t('signIn.otpVerification.codeHelper')}</p>
            </div>

            {error && (
              <div className="bg-destructive/10 rounded-md p-3">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  {t('signIn.otpVerification.verifying')}
                </div>
              ) : (
                t('signIn.otpVerification.verifyButton')
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setVerifyingOTP(false);
                setCode('');
                setError('');
              }}
              disabled={loading}
            >
              {t('signIn.otpVerification.backButton')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side - Form */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language Selector */}
          <div className="mb-4 flex justify-end">
            <LanguageSelector />
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <BrandLogo brand={brand} />
            </div>
            <div className="space-y-2 text-center">
              <h1 className="text-foreground text-3xl font-bold tracking-tight">{t('signIn.title')}</h1>
              <p className="text-muted-foreground">{t('signIn.subtitle')}</p>
            </div>

            <form onSubmit={handleOTPSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('signIn.email')}</Label>
                <Input
                  id="email"
                  placeholder={t('signIn.emailPlaceholder')}
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={loading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="bg-muted/50 rounded-lg border p-4">
                <p className="text-muted-foreground text-sm">{t('signIn.otpInfo')}</p>
              </div>

              {error && (
                <div className="bg-destructive/10 rounded-md p-3">
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              )}

              <Button className="w-full" type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    {t('signIn.sendingCode')}
                  </div>
                ) : (
                  t('signIn.sendCode')
                )}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="border-border w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background text-muted-foreground px-2">{t('signIn.orContinueWith')}</span>
              </div>
            </div>

            <Button
              variant="outline"
              type="button"
              className="w-full"
              disabled={loading}
              onClick={handleGoogleSignIn}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('signIn.continueWithGoogle')}
            </Button>

            <p className="text-muted-foreground text-center text-sm">
              {t('signIn.noAccount')}{' '}
              <Link
                to={signUpTarget}
                className="text-foreground hover:text-primary font-semibold underline underline-offset-4"
              >
                {t('signIn.signUpLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Stats showcase (hidden when whitelabeled for a partner;
          marketing copy is tagada-specific and out of place under a partner brand) */}
      {!isPartnerBrand && (
      <div className="border-border bg-card hidden flex-1 items-center justify-center border-l p-12 lg:flex">
        <div className="max-w-lg space-y-8">
          <div className="space-y-4">
            <h2 className="text-card-foreground text-balance text-4xl font-bold">{t('signIn.marketing.welcomeTitle')}</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('signIn.marketing.welcomeSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-card-foreground text-4xl font-bold">{t('signIn.marketing.stats.uptime')}</p>
              <p className="text-muted-foreground text-sm">{t('signIn.marketing.stats.uptimeLabel')}</p>
            </div>
            <div className="space-y-2">
              <p className="text-card-foreground text-4xl font-bold">{t('signIn.marketing.stats.support')}</p>
              <p className="text-muted-foreground text-sm">{t('signIn.marketing.stats.supportLabel')}</p>
            </div>
            <div className="space-y-2">
              <p className="text-card-foreground text-4xl font-bold">{t('signIn.marketing.stats.countries')}</p>
              <p className="text-muted-foreground text-sm">{t('signIn.marketing.stats.countriesLabel')}</p>
            </div>
            <div className="space-y-2">
              <p className="text-card-foreground text-4xl font-bold">{t('signIn.marketing.stats.processed')}</p>
              <p className="text-muted-foreground text-sm">{t('signIn.marketing.stats.processedLabel')}</p>
            </div>
          </div>

          <div className="border-border space-y-4 border-t pt-6">
            <p className="text-card-foreground text-sm font-semibold">{t('signIn.marketing.featuresTitle')}</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="bg-primary mt-2 h-2 w-2 rounded-full" />
                <div className="space-y-1">
                  <p className="text-card-foreground text-sm">{t('signIn.marketing.feature1.title')}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('signIn.marketing.feature1.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary mt-2 h-2 w-2 rounded-full" />
                <div className="space-y-1">
                  <p className="text-card-foreground text-sm">{t('signIn.marketing.feature2.title')}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('signIn.marketing.feature2.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary mt-2 h-2 w-2 rounded-full" />
                <div className="space-y-1">
                  <p className="text-card-foreground text-sm">{t('signIn.marketing.feature3.title')}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('signIn.marketing.feature3.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary mt-2 h-2 w-2 rounded-full" />
                <div className="space-y-1">
                  <p className="text-card-foreground text-sm">{t('signIn.marketing.feature4.title')}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('signIn.marketing.feature4.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary mt-2 h-2 w-2 rounded-full" />
                <div className="space-y-1">
                  <p className="text-card-foreground text-sm">{t('signIn.marketing.feature5.title')}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('signIn.marketing.feature5.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
